import { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: 'ri-book-open-line',
    title: '교회사 연구자',
    desc: '역사 속 믿음의 발자취를 탐구하며 깊이 있는 연구 활동을 이어갑니다.',
    color: '#f59e0b',
    bg: 'rgba(255,255,255,0.08)',
    borderColor: 'rgba(255,255,255,0.15)',
    image: 'https://public.readdy.ai/ai/img_res/907e8b82d9c9dcad213b9f1535147d73.jpg',
  },
  {
    icon: 'ri-live-line',
    title: '쇼호스트',
    desc: '라이브커머스 현장에서 고객과 직접 소통하는 전문 쇼호스트입니다.',
    color: '#C9F53B',
    bg: 'rgba(255,255,255,0.08)',
    borderColor: 'rgba(255,255,255,0.15)',
    image: 'https://readdy.ai/api/search-image?query=professional%20female%20live%20streaming%20host%20bright%20modern%20studio%20ring%20light%20colorful%20product%20display%20energetic%20vibrant%20atmosphere%20mint%20green%20teal%20color%20scheme%20contemporary%20broadcast%20setup%20smiling%20presenter%20dynamic%20lighting&width=600&height=400&seq=about_show1&orientation=landscape',
  },
  {
    icon: 'ri-robot-line',
    title: 'AI 강사',
    desc: 'AI 시대를 함께 열어가는 교육 전문가로 누구나 쉽게 배울 수 있도록 돕습니다.',
    color: '#a3e635',
    bg: 'rgba(255,255,255,0.08)',
    borderColor: 'rgba(255,255,255,0.15)',
    image: 'https://readdy.ai/api/search-image?query=futuristic%20digital%20interface%20holographic%20AI%20assistant%20visualization%20glowing%20network%20connections%20abstract%20technology%20background%20soft%20violet%20lavender%20gradient%20modern%20data%20visualization%20elegant%20pastel%20purple%20tones%20innovative%20education&width=600&height=400&seq=about_ai1&orientation=landscape',
  },
];

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="w-full py-20 md:py-32 px-4 md:px-8"
      style={{ backgroundColor: '#0047AB' }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20">

          {/* 왼쪽: 큰 헤드라인 */}
          <div
            className="lg:w-5/12 flex-shrink-0"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(40px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#DFFF00' }}></span>
              <span className="text-sm font-semibold tracking-wide" style={{ color: '#DFFF00' }}>사람아이앤지</span>
            </div>

            <h2
              className="leading-[1.2] tracking-tight mb-12 mt-6"
              style={{
                fontFamily: "'SeolibcheYugeonwook', 'establishRetrosansOTF', sans-serif",
                fontWeight: 700,
                color: '#DFFF00',
                fontSize: 'clamp(2rem, 4vw, 3.8rem)',
                wordBreak: 'keep-all',
              }}
            >
              낯선 것은 친숙하게,<br />
              평범한 것은 특별하게.
            </h2>

            <p className="text-lg md:text-xl leading-relaxed mb-10 font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>
              누구나 쉽게 배우는 AI 교육부터,<br />
              잊혀진 교회사의 숨결을 깨우는 이야기,<br />
              그리고 진심을 다하는 라이브커머스까지.<br />
              당신의 삶에 친절한 동행을 시작합니다.
            </p>

            {/* 프로필 */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 shadow-md ring-2" style={{ ringColor: '#C9F53B' }}>
                <img
                  src="https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/bbda389253b7796ac9a3473b61641e88.png"
                  alt="어정희 프로필"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <p className="text-lg font-extrabold text-white">어정희</p>
                <p className="text-sm font-medium tracking-widest" style={{ color: 'rgba(255,255,255,0.5)' }}>EUH JUNG HEE</p>
              </div>
            </div>
          </div>

          {/* 오른쪽: 카드 그리드 */}
          <div className="lg:w-7/12 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-5">
            {features.map((item, i) => (
              <div
                key={item.title}
                className="group flex items-start gap-5 rounded-3xl p-6 border hover:border-white/30 transition-all duration-300 cursor-pointer"
                style={{
                  backgroundColor: item.bg,
                  borderColor: item.borderColor,
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(40px)',
                  transition: `opacity 0.7s ease ${0.15 + i * 0.12}s, transform 0.7s ease ${0.15 + i * 0.12}s, box-shadow 0.3s ease`,
                }}
              >
                {/* 이미지 썸네일 */}
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden flex-shrink-0 shadow-md">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* 텍스트 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ backgroundColor: item.color + '33' }}
                    >
                      <i className={`${item.icon} text-base`} style={{ color: item.color }}></i>
                    </div>
                    <h3 className="text-base md:text-lg font-extrabold text-white">{item.title}</h3>
                  </div>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{item.desc}</p>
                </div>

                {/* 화살표 */}
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ backgroundColor: item.color + '33' }}
                >
                  <i className="ri-arrow-right-line text-sm" style={{ color: item.color }}></i>
                </div>
              </div>
            ))}

            {/* 통계 배너 */}
            <div
              className="rounded-3xl p-6 flex items-center justify-around gap-4"
              style={{
                backgroundColor: '#DFFF00',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(40px)',
                transition: `opacity 0.7s ease ${0.15 + features.length * 0.12}s, transform 0.7s ease ${0.15 + features.length * 0.12}s`,
              }}
            >
              {[
                { num: '3+', label: '전문 분야' },
                { num: '10+', label: '활동 연수' },
                { num: '500+', label: '강의 수강생' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-3xl md:text-4xl font-extrabold text-gray-900">{stat.num}</p>
                  <p className="text-xs md:text-sm font-semibold text-gray-700 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
